import type {ReactElement} from 'react';
import React, {useMemo, useState} from 'react';
import {ContextualMenu} from '@Components/contextual-menu';
import {ItemHeaderTab} from '@Components/poster-editor/components/poster-editing-side-panel/poster-editing-side-panel.types';
import {ItemPositionContextualOptions} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-position-contextual-options';
import {PosterItemControls} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-item-controls';
import {ItemOpacityOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-opacity-option';
import {ContextualMenuHeadingOption} from '@Components/contextual-menu/components/contextual-menu-heading-option';
import {ItemWithDualFontOptions} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-with-dual-font-options';
import {ItemSizeOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-size-option';
import {TextColorOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/text-color-option';
import {TextOutlineOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/text-outline-option';
import {ItemLineHeightOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-line-height-option';
import {ItemLetterSpacingOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-letter-spacing-option';
import {LayoutOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/layout-option';
import {MenuPanels} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-editing-menu-options/poster-editing-menu-options.types';
import {ContextualMenuTextHeader} from '@Components/contextual-menu/components/contextual-menu-text-header';
import {ContextualMenuHeader} from '@Components/contextual-menu/components/contextual-menu-header';
import {MenuLayoutSelector} from '@Components/poster-editor/components/poster-editing-side-panel/components/menu-layout-selector';
import {TextFillColorPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/text-fill-color-panel';
import {TextOutlineColorPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/text-outline-color-panel';
import {LayoutBackgroundOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/layout-background-option';
import {LayoutBackgroundColorPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/layout-background-color-panel';
import {ItemBorderOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-border-option';
import {ItemBorderColorPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-border-color-panel';
import {ItemVerticalSpacingOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-vertical-spacing-option';
import {ItemInteractivityLinkOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-interactivity-link-option';
import {InformationLinkOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/information-link-option';
import {MenuIconSizeOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/menu-icon-size-option';
import {MenuIconColorOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/menu-icon-color-option';
import {MenuIconColorPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/menu-icon-color-panel';
import {LayoutOptions} from '@Components/contextual-menu/components/contextual-menu-layout-button';
import {MenuEditContentOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/menu-edit-content-option';
import {ItemLockInformationBox} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-lock-information-box';
import {ItemWithDualFontsSelector} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-with-dual-fonts-selector';
import {FontFamilyOptionType} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-with-dual-font-options/item-with-dual-font-options.types';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useIsOpenedMenuPanelValid} from '@Hooks/poster-editor/useIsOpenedMenuPanelValid';
import {ItemAnimationOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-animation-option/item-animation-option';
import {ItemAnimationPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-animation-panel/item-animation-panel';
import {ContextualMenuContentKeys} from '@Components/poster-editor/components/poster-editing-side-panel/components/contextual-options/contextual-options.types';

export function PosterEditingMenuOptions(): ReactElement<any> {
  const activeItemId = useSingleActivePosterItemId();
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const [currentItemHeaderTab, setCurrentItemHeaderTab] = useState(ItemHeaderTab.EDIT);
  const [currentlyOpenedPanel, setCurrentlyOpenedPanel] = useState(MenuPanels.NONE);
  useIsOpenedMenuPanelValid(currentlyOpenedPanel, setCurrentlyOpenedPanel);

  if (!activeItem) {
    throw new Error('Active item does not exist in table options');
  }

  if (!activeItem.isMenu()) {
    throw new Error(`Expected item to be of type Menu - instead got ${activeItem.gitype}`);
  }

  const getHeaderText = (): string => {
    if (currentItemHeaderTab === ItemHeaderTab.POSITION) {
      return window.i18next.t('pmwjs_position');
    }
    if (currentlyOpenedPanel === MenuPanels.LAYOUT) {
      return window.i18next.t('pmwjs_layout');
    } else if (currentlyOpenedPanel === MenuPanels.FILL) {
      return window.i18next.t('pmwjs_fill');
    } else if (currentlyOpenedPanel === MenuPanels.ANIMATION) {
      return window.i18next.t('pmwjs_animation');
    } else {
      return window.i18next.t('pmwjs_color');
    }
  };

  const getHeader = (): ReactElement<any> | undefined => {
    if (showDefaultOptions() && currentItemHeaderTab === ItemHeaderTab.EDIT) {
      return undefined;
    }

    if (isFontSelectorOpened()) {
      return undefined;
    }
    return (
      <ContextualMenuHeader id="menu-layout-panel-header">
        <ContextualMenuTextHeader
          text={getHeaderText()}
          showBackButton
          onBackButton={(): void => {
            if (currentItemHeaderTab === ItemHeaderTab.POSITION) {
              setCurrentItemHeaderTab(ItemHeaderTab.EDIT);
            }
            setCurrentlyOpenedPanel(MenuPanels.NONE);
          }}
        />
      </ContextualMenuHeader>
    );
  };

  const getPositionOptions = (): ReactElement<any> => {
    return <ItemPositionContextualOptions />;
  };

  const getEditOptions = (): ReactElement<any> => {
    return (
      <>
        <ItemLockInformationBox />
        <PosterItemControls
          onClickPosition={(): void => {
            setCurrentItemHeaderTab(ItemHeaderTab.POSITION);
          }}
        />
        <ItemOpacityOption />
        <ItemAnimationOption
          onAnimationTypeClick={(): void => {
            setCurrentlyOpenedPanel(MenuPanels.ANIMATION);
          }}
        />
        <MenuEditContentOption />
        <LayoutOption
          layoutFor={LayoutOptions.MENU}
          onClickLayout={(): void => {
            setCurrentlyOpenedPanel(MenuPanels.LAYOUT);
          }}
        />
        <ContextualMenuHeadingOption id="menu-text-styles-option" text={window.i18next.t('pmwjs_text_styles')} />
        <ItemWithDualFontOptions
          onClickFontButton={(clickedFontOption): void => {
            switch (clickedFontOption) {
              case FontFamilyOptionType.ONE:
                setCurrentlyOpenedPanel(MenuPanels.FONT_ONE);
                break;
              case FontFamilyOptionType.TWO:
                setCurrentlyOpenedPanel(MenuPanels.FONT_TWO);
                break;
              default:
            }
          }}
        />
        <ItemSizeOption />
        <TextColorOption
          onClickColorFill={(): void => {
            setCurrentlyOpenedPanel(MenuPanels.FILL);
          }}
        />
        <TextOutlineOption
          onClickColorFill={(): void => {
            setCurrentlyOpenedPanel(MenuPanels.OUTLINE);
          }}
        />
        <ItemLineHeightOption addDelayToOnChange />
        <ItemLetterSpacingOption />
        <ContextualMenuHeadingOption id="menu-layout-styles-option" text={window.i18next.t('pmwjs_layout_styles')} />
        <LayoutBackgroundOption
          onClickColorFill={(): void => {
            setCurrentlyOpenedPanel(MenuPanels.BACKGROUND);
          }}
        />
        <ItemBorderOption
          onClickColorFill={(): void => {
            setCurrentlyOpenedPanel(MenuPanels.BORDER);
          }}
        />
        <ItemVerticalSpacingOption />
        <ContextualMenuHeadingOption id="menu-icon-styles-option" text={window.i18next.t('pmwjs_icon_styles')} />
        <MenuIconColorOption
          onClickColorFill={(): void => {
            setCurrentlyOpenedPanel(MenuPanels.ICON_COLOR);
          }}
        />
        <MenuIconSizeOption />
        <ContextualMenuHeadingOption id="menu-interactivity-option" text={window.i18next.t('pmwjs_interactivity')} />
        <ItemInteractivityLinkOption />
        <InformationLinkOption />
      </>
    );
  };

  const getDefaultOptions = (): ReactElement<any> => {
    switch (currentItemHeaderTab) {
      case ItemHeaderTab.EDIT:
        return getEditOptions();
      case ItemHeaderTab.POSITION:
        return getPositionOptions();
      default:
        throw new Error('unhandled tab item for contextual header');
    }
  };

  const getPanels = (): ReactElement<any> => {
    switch (currentlyOpenedPanel) {
      case MenuPanels.LAYOUT:
        return <MenuLayoutSelector />;
      case MenuPanels.FILL:
        return <TextFillColorPanel isForLayoutItem />;
      case MenuPanels.OUTLINE:
        return <TextOutlineColorPanel />;
      case MenuPanels.BACKGROUND:
        return <LayoutBackgroundColorPanel />;
      case MenuPanels.BORDER:
        return <ItemBorderColorPanel />;
      case MenuPanels.ICON_COLOR:
        return <MenuIconColorPanel />;
      case MenuPanels.ANIMATION:
        return <ItemAnimationPanel />;
      case MenuPanels.FONT_ONE:
        return (
          <ItemWithDualFontsSelector
            onBack={(): void => {
              setCurrentlyOpenedPanel(MenuPanels.NONE);
            }}
          />
        );
      case MenuPanels.FONT_TWO:
        return (
          <ItemWithDualFontsSelector
            font={FontFamilyOptionType.TWO}
            onBack={(): void => {
              setCurrentlyOpenedPanel(MenuPanels.NONE);
            }}
          />
        );

      default:
        throw new Error(`unhandled sub panel option ${currentlyOpenedPanel} for menu item`);
    }
  };

  const isFontSelectorOpened = (): boolean => {
    return currentlyOpenedPanel === MenuPanels.FONT_ONE || currentlyOpenedPanel === MenuPanels.FONT_TWO;
  };

  const showDefaultOptions = (): boolean => {
    return currentlyOpenedPanel === MenuPanels.NONE;
  };

  const getOptions = (): ReactElement<any> => {
    if (!showDefaultOptions()) {
      return getPanels();
    }

    return getDefaultOptions();
  };

  const getKey = (): string => {
    if (showDefaultOptions()) {
      return currentItemHeaderTab === ItemHeaderTab.POSITION ? ContextualMenuContentKeys.MENU_DEFAULT_POSITION : ContextualMenuContentKeys.MENU_DEFAULT_EDIT;
    }

    return '';
  };

  return useMemo((): ReactElement<any> => {
    return (
      <ContextualMenu
        contentClassName={isFontSelectorOpened() ? 'spacing-p-0' : ''}
        id="menu-contextual-options"
        includeLeftRightPadding={showDefaultOptions()}
        header={getHeader()}
        contentKey={getKey()}
      >
        {getOptions()}
      </ContextualMenu>
    );
  }, [currentItemHeaderTab, currentlyOpenedPanel]);
}
